import { Col, message, Modal, Row } from 'antd';
import { Formik } from 'formik';
import { DatePicker, Form, Input, InputNumber, Select, SubmitButton } from 'formik-antd';
import moment from 'moment';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';
import { useGetAllMemberQuery } from '../member/membersAPI';
import { useCreateLoanMutation } from './loanAPI';

const { Option } = Select;

const DATE_FORMAT = 'DD/MM/YYYY';

const LOAN_TYPE_LIST = [
  'Pinjaman Regular',
  'Pinjaman Pendidikan',
  'Pinjaman Kepemilikan Rumah',
  'Pinjaman Usaha/Produktif',
  'Pinjaman Darurat',
  'Pinjaman Karyawan',
  'Pinjaman Deposito Sicundo',
  'Pinjaman Kendaraan',
  'Pinjaman Kapitalisasi',
];

interface CreateLoanForm {
  memberId: number | null;
  accountId: string;
  borrowDate: any;
  borrowAmount: number;
  dueDate: number;
  installment: number;
  lastTransaction: any;
  loanType: string;
  remainingAmount: number;
  interestType: string;
  restructurePolicy: string;
  commitment: string;
  additionalInfoDate: any;
}

function CreateLoan() {
  const navigate = useNavigate();
  const { data: memberListData, isLoading: memberListDataIsLoading, isError: memberListDataIsError } = useGetAllMemberQuery(true);
  const [createLoan] = useCreateLoanMutation();

  const validate = (value) => {
    let error;
    if (!value && value !== 0) {
      error = 'Tidak boleh kosong';
    }
    return error;
  };

  const save = async (value: CreateLoanForm, closeLoading: any) => {
    const hasAdditionalInfo = value.restructurePolicy?.trim() !== '' || value.commitment?.trim() !== '';

    const additionalInfo = hasAdditionalInfo
      ? [
          {
            date: moment(value.additionalInfoDate).format(DATE_FORMAT),
            isActive: true,
            restructurePolicy: value.restructurePolicy,
            commitment: value.commitment,
          },
        ]
      : [];

    const payload = {
      memberId: Number(value.memberId),
      accountId: value.accountId,
      borrowDate: moment(value.borrowDate).format('YYYY-MM-DD'),
      borrowAmount: Number(value.borrowAmount),
      dueDate: Number(value.dueDate),
      installment: Number(value.installment),
      isPaid: false,
      interestType: value.interestType,
      additionalInfo,
      lastTransaction: moment(value.lastTransaction).format('YYYY-MM-DD'),
      loanType: value.loanType,
      remainingAmount: Number(value.remainingAmount || value.borrowAmount),
    };

    createLoan(payload)
      .unwrap()
      .then(() => {
        closeLoading();
        message.success('Berhasil menambahkan pinjaman');
        navigate('/loan');
      })
      .catch(() => {
        closeLoading();
        message.error('Gagal menambahkan pinjaman');
      });
  };

  return (
    <Modal
      title={`Input Data Pinjaman`}
      width={800}
      visible={true}
      destroyOnClose={true}
      onCancel={() => navigate('/loan')}
      okButtonProps={{ hidden: true }}
      cancelButtonProps={{ hidden: true }}
      footer={null}
    >
      <ContentWithErrorHandler isError={memberListDataIsError} isLoading={memberListDataIsLoading} withBox={false}>
        <Formik
          initialValues={{
            memberId: null,
            accountId: '',
            borrowDate: moment(),
            borrowAmount: 0,
            dueDate: 1,
            installment: 1,
            preferredPayment: 0,
            interestType: 'declining',
            paymentPickupDate: moment(),
            lastTransaction: moment(),
            loanType: 'Pinjaman Regular',
            remainingAmount: 0,
            restructurePolicy: '',
            commitment: '',
            additionalInfoDate: moment(),
          }}
          onSubmit={async (values, actions) => {
            const hide = message.loading('Loading', 0);
            await save(values, hide);
            actions.setSubmitting(false);
          }}
          enableReinitialize={true}
          render={() => (
            <Form layout="vertical">
              <Row gutter={[8, 0]}>
                <Col span={24}>
                  <Form.Item name="memberId" label="Anggota" validate={validate}>
                    <Select
                      fast
                      showSearch
                      name="memberId"
                      style={{ width: '100%' }}
                      placeholder="Pilih anggota"
                      optionFilterProp="children"
                      filterOption={(input, option: any) => option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    >
                      {memberListData?.map((member) => {
                        return (
                          <Option key={member.id} value={member.id}>
                            {member.memberId} - {member.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="accountId" label="Nomor Rekening" validate={validate}>
                    <Input name="accountId" placeholder="Masukkan nomor rekening" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="loanType" label="Jenis Pinjaman" validate={validate}>
                    <Select fast name="loanType" style={{ width: '100%' }} placeholder="Pilih jenis pinjaman">
                      {LOAN_TYPE_LIST.map((loanType) => {
                        return (
                          <Option key={loanType} value={loanType}>
                            {loanType}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="borrowDate" label="Tanggal Pinjam" validate={validate}>
                    <DatePicker name="borrowDate" format={DATE_FORMAT} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="interestType" label="Tipe Bunga">
                    <Select fast name="interestType" style={{ width: '100%' }} placeholder="Pilih tipe bunga">
                      <Option value="declining">Menurun</Option>
                      <Option value="effective">Efektif</Option>
                      <Option value="flat">Flat</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="lastTransaction" label="Transaksi Terakhir" validate={validate}>
                    <DatePicker name="lastTransaction" format={DATE_FORMAT} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dueDate" label="Tanggal Jatuh Tempo" validate={validate}>
                    <InputNumber name="dueDate" min={1} max={31} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="borrowAmount" label="Jumlah Pinjaman" validate={validate}>
                    <InputNumber
                      name="borrowAmount"
                      formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value?.replace(/Rp\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="remainingAmount" label="Sisa Saldo Pinjaman">
                    <InputNumber
                      name="remainingAmount"
                      formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={(value: any) => value?.replace(/Rp\s?|(,*)/g, '')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item name="installment" label="Cicilan (kali)" validate={validate}>
                    <InputNumber name="installment" min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <h4>Informasi Tambahan (Opsional)</h4>
                </Col>
                <Col span={12}>
                  <Form.Item name="additionalInfoDate" label="Tanggal Informasi Tambahan">
                    <DatePicker name="additionalInfoDate" format={DATE_FORMAT} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="restructurePolicy" label="Kebijakan Restruktur">
                    <Input.TextArea name="restructurePolicy" rows={3} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="commitment" label="Komitmen Anggota">
                    <Input.TextArea name="commitment" rows={3} />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="end" style={{ marginTop: 20 }}>
                <SubmitButton>Simpan</SubmitButton>
              </Row>
            </Form>
          )}
        />
      </ContentWithErrorHandler>
    </Modal>
  );
}

export default CreateLoan;
